/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { BenefitTypes, RouteCodeOptions, RouteCodes } from '@strata/constants';
import { ActionButtonText } from '@strata/react-buttons';
import { Checkbox } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import { FieldArray, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import * as Yup from 'yup';

import { ListPageHeader } from 'components/ListPageHeader';
import { RouteConditionDialog } from 'pages/routeManagement/dialogs/RouteConditionDialog';
import { PayersAndSequencing } from 'pages/routeManagement/PayersAndSequencing';
import { RateOverrideInputs } from 'pages/routeManagement/RateOverrideInputs';
import { RouteConditionValue } from 'pages/routeManagement/RouteConditionValue';
import {
    ConditionType,
    ConditionTypeNameMap,
    getInferredSupplierDisplayName,
    getInitialPlacement,
    isRouteExpired,
    SendToOptions,
} from 'services/RouteRuleHelpers';

const CategoryOptions = [
    'CONTINUITY OF CARE ROFR',
    'DIRECT ASSIGNMENT',
    'GRANDFATHERED',
    'HSSP (HEALTH SYSTEM) ROFR',
    'LIVE REFERRALS',
    'OTHER',
    'OTHER ROFR',
    'PRINCIPAL PROVIDER',
    'RESEARCH',
];

const routeCodeOptions = RouteCodeOptions.filter(({ value }) =>
    [
        RouteCodes.CONTRACT,
        RouteCodes.DEFAULT,
        RouteCodes.DIRECT,
        RouteCodes.RESEARCH,
        RouteCodes.ROFR,
        RouteCodes.UNMANAGED,
        RouteCodes.ACCEPT,
    ].includes(value),
);

/** Route codes where send_to option is displayed */
const SendToRouteCodes = [
    RouteCodes.CONTRACT,
    RouteCodes.DIRECT,
    RouteCodes.ROFR,
    RouteCodes.ACCEPT,
];

const getInitialConditions = (rule) =>
    Object.values(ConditionType)
        .filter((condition) => rule?.[condition] != null)
        .map((condition) => ({
            condition_type: condition,
            value:
                condition === ConditionType.PRODUCT
                    ? {
                          product_service_oid: rule?.product_service_oid,
                          is_brand: rule?.is_brand,
                      }
                    : rule[condition],
        }));

/** Get the initial value for send_to based on route_code and presence of network_list_id or supplier_npi */
const getInitialSendTo = (routeRule) => {
    if (routeRule?.network_list_id) {
        return SendToOptions.NETWORK_LIST;
    }
    if (routeRule?.supplier_npi) {
        return SendToOptions.PHARMACY;
    }
    if (SendToRouteCodes.includes(routeRule?.route_code)) {
        return SendToOptions.INFERRED_SUPPLIER;
    }
    return undefined;
};

/** Build a payer segment for each segment the rule is configured in, `currentSegmentId` first */
const getInitialPayerSegments = (configuredSegments, routesBySegment, currentSegmentId) =>
    [...(configuredSegments ?? [])]
        .sort(
            (a, b) =>
                Number(b.segment_id === currentSegmentId) -
                Number(a.segment_id === currentSegmentId),
        )
        .map((segment) => {
            const existing = segment.route_rule_id
                ? {
                      route_rule_id: segment.route_rule_id,
                      route_sequence: segment.route_sequence,
                      before_continuity: segment.before_continuity,
                  }
                : undefined;

            return {
                distinct_id: segment.segment_id,
                payer_id: segment.payer_id,
                segment_id: segment.segment_id,
                existing,
                start_date: segment.start_date,
                end_date: segment.end_date ?? undefined,
                // Each segment pre populates the placement its rule has in that segment's routes.
                // An expired rule has no place left in them, so it is sequenced in again.
                ...(existing &&
                    !isRouteExpired(segment.end_date) &&
                    getInitialPlacement(routesBySegment?.[segment.segment_id], existing)),
            };
        });

/**
 * Every field a route rule and a global route have in common, from its display name down to its
 * rate override. The page above supplies the heading, the global route controls, and the submit.
 */
export const RouteRuleForm = ({
    benefit = undefined,
    conditionOptionsMap,
    payerSegmentOptions = undefined,
    configuredSegmentRoutes = undefined,
    currentSegmentId = undefined,
}) => {
    const formik = useFormikContext();
    const [conditionToEdit, setConditionToEdit] = useState();

    const networkOptions = conditionOptionsMap[ConditionType.NETWORK_LIST];
    const supplierOptions = conditionOptionsMap[ConditionType.SUPPLIER];

    return (
        <>
            <TextInputFormik
                id="route_name"
                label="Display Name"
                required
                helperText="This will display in internal and external applications, to both pharmacy and payer users. e.g. Referral timeline, supplier fax, etc."
            />
            <TextAreaFormik
                id="route_description"
                label="Description"
                required
                helperText="This will provide additional context for this rule in internal and external applications, to both pharmacy and payer users. e.g. Referral timeline, supplier fax, etc."
            />
            <TextAreaFormik
                id="internal_note"
                label="Internal Note"
                helperText="This note will provide additional context for this rule in internal applications only. Markdown-like links may be used in the format of [text](url)."
            />
            <SuggestSingleSelectFormik
                id="category"
                label="Route Category"
                required
                helperText="Used for categorization of routes for reporting or filtering purposes."
                values={CategoryOptions}
            />
            <PayersAndSequencing
                benefit={benefit}
                payerSegmentOptions={payerSegmentOptions}
                configuredSegmentRoutes={configuredSegmentRoutes}
                currentSegmentId={currentSegmentId}
            />
            <ListPageHeader
                pageTitle="Route Rule Condition(s)"
                rightContent={
                    <ActionButtonText
                        leftIcon={IconNames.ADD}
                        label="Add Condition"
                        onClick={() => setConditionToEdit({})}
                    />
                }
            />
            <FieldArray name="conditions">
                {(arrayHelpers) => (
                    <div className="sds_route-rule-conditions">
                        <RouteConditionDialog
                            condition={conditionToEdit}
                            conditionOptionsMap={conditionOptionsMap}
                            onClose={() => setConditionToEdit()}
                            onSave={(condition) =>
                                conditionToEdit?.index != null
                                    ? arrayHelpers.replace(conditionToEdit.index, condition)
                                    : arrayHelpers.push(condition)
                            }
                        />
                        {formik.values[arrayHelpers.name].length === 0 ? (
                            <div className="sds_route-rule-conditions--no-conditions">
                                No conditions added.
                            </div>
                        ) : (
                            formik.values[arrayHelpers.name].map((condition, index) => (
                                <div
                                    key={condition.condition_type}
                                    className="sds_route-rule-conditions__condition"
                                >
                                    <DetailLabelValuePair
                                        className="sds_route-rule-conditions__condition__label"
                                        label={ConditionTypeNameMap[condition.condition_type]}
                                        value={
                                            <RouteConditionValue
                                                condition={condition}
                                                conditionOptionsMap={conditionOptionsMap}
                                            />
                                        }
                                    />
                                    <div className="sds_route-rule-conditions__condition__actions">
                                        <Icon
                                            icon={IconNames.EDIT}
                                            title="Edit"
                                            onClick={() =>
                                                setConditionToEdit({ ...condition, index })
                                            }
                                        />
                                        <Icon
                                            icon={IconNames.TRASH}
                                            title="Delete"
                                            onClick={() => arrayHelpers.remove(index)}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </FieldArray>
            <ListPageHeader pageTitle="Route Rule Action" />
            <SuggestSingleSelectFormik
                id="route_code"
                label="Route Code"
                required
                values={routeCodeOptions}
                onChange={(newValue) =>
                    formik.setValues({
                        ...formik.values,
                        route_code: newValue?.value,
                        send_to:
                            newValue?.value === RouteCodes.DEFAULT
                                ? SendToOptions.NETWORK_LIST // send_to is always NETWORK_LIST for DEFAULT routes
                                : undefined,
                        supplier_npi: undefined,
                        network_list_id: undefined,
                    })
                }
                selectedItem={routeCodeOptions.find(
                    ({ value }) => value === formik.values.route_code,
                )}
            />
            <Checkbox
                name="ignore_capabilities"
                label="Ignore Payer Capabilities"
                className="margin__bottom--medium"
                checked={formik.values.ignore_capabilities}
                onChange={(checked) => formik.setFieldValue('ignore_capabilities', checked)}
            />
            {formik.values.route_code && SendToRouteCodes.includes(formik.values.route_code) && (
                <RadioButtonGroupFormik
                    name="send_to"
                    label="Send Referral To"
                    required
                    options={[
                        {
                            value: SendToOptions.PHARMACY,
                            text: 'Single Pharmacy',
                        },
                        {
                            value: SendToOptions.INFERRED_SUPPLIER,
                            text: getInferredSupplierDisplayName(formik.values),
                        },
                        ...(formik.values.route_code === RouteCodes.DIRECT
                            ? [
                                  {
                                      value: SendToOptions.NETWORK_LIST,
                                      text: 'Round robin within Network List',
                                  },
                              ]
                            : []),
                    ]}
                    onChange={(newValue) =>
                        formik.setValues({
                            ...formik.values,
                            send_to: newValue,
                            supplier_npi: undefined,
                            network_list_id: undefined,
                        })
                    }
                />
            )}
            {formik.values.send_to === SendToOptions.NETWORK_LIST && (
                <SuggestSingleSelectFormik
                    id="network_list_id"
                    label="Network"
                    required
                    values={networkOptions}
                    isLoading={!networkOptions}
                    onChange={(newValue) =>
                        formik.setFieldValue('network_list_id', newValue?.value)
                    }
                    selectedItem={networkOptions?.find(
                        (network) => network.value === formik.values.network_list_id,
                    )}
                />
            )}
            {formik.values.send_to === SendToOptions.PHARMACY && (
                <SuggestSingleSelectFormik
                    id="supplier_npi"
                    label="Single Pharmacy"
                    required
                    values={supplierOptions}
                    isLoading={!supplierOptions}
                    onChange={(newValue) =>
                        formik.setFieldValue('supplier_npi', newValue?.supplier_npi)
                    }
                    selectedItem={supplierOptions?.find(
                        (supplier) => supplier.supplier_npi === formik.values.supplier_npi,
                    )}
                />
            )}
            <RateOverrideInputs />
        </>
    );
};

RouteRuleForm.propTypes = {
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
    conditionOptionsMap: PropTypes.shape({}).isRequired,
    payerSegmentOptions: PropTypes.arrayOf(
        PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }),
    ),
    configuredSegmentRoutes: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
    ),
    currentSegmentId: PropTypes.string,
};

RouteRuleForm.initialValues = (
    routeRule,
    configuredSegments,
    routesBySegment,
    currentSegmentId,
) => ({
    route_name: routeRule?.route_name,
    route_description: routeRule?.route_description,
    internal_note: routeRule?.internal_note ?? undefined,
    payer_segments: getInitialPayerSegments(configuredSegments, routesBySegment, currentSegmentId),
    route_code: routeRule?.route_code,
    ignore_capabilities: routeRule?.ignore_capabilities ?? false,
    global_route: !!routeRule?.is_global,
    category: routeRule?.category,
    send_to: getInitialSendTo(routeRule),
    supplier_npi: routeRule?.supplier_npi ?? undefined,
    network_list_id: routeRule?.network_list_id ?? undefined,
    conditions: getInitialConditions(routeRule),
    ...RateOverrideInputs.initialValues(routeRule),
});

RouteRuleForm.validationSchema = {
    route_name: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .max(100, validationStrings.maxCharacter[100]),
    route_description: Yup.string()
        .required(validationStrings.requiredField.errorMessage)
        .max(250, validationStrings.maxCharacter[250]),
    internal_note: Yup.string().max(250, validationStrings.maxCharacter[250]),
    category: Yup.string().required(validationStrings.requiredField.errorMessage),
    payer_segments: Yup.array()
        .of(
            Yup.object({
                segment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
                sequence: Yup.string().required(validationStrings.requiredField.errorMessage),
                rule_placement: Yup.string().required(validationStrings.requiredField.errorMessage),
                start_date: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                end_date: DateInputFormik.validator(),
            }),
        )
        // A global route can be configured in no payer segments at all; a rule edited within one
        // segment always belongs to that segment
        .when('global_route', {
            is: true,
            otherwise: (schema) => schema.min(1, validationStrings.requiredField.errorMessage),
        }),
    conditions: Yup.array()
        .required(validationStrings.requiredField.errorMessage)
        .min(1, validationStrings.requiredField.errorMessage),
    route_code: Yup.string().required(validationStrings.requiredField.errorMessage),
    send_to: Yup.string().when('route_code', {
        is: (code) => SendToRouteCodes.includes(code),
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    network_list_id: Yup.string().when('send_to', {
        is: SendToOptions.NETWORK_LIST,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    supplier_npi: Yup.string().when('send_to', {
        is: SendToOptions.PHARMACY,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    ...RateOverrideInputs.validationSchema,
};

/** The rule itself, without the placement that differs per payer segment */
RouteRuleForm.submitValues = (values) => {
    const body = {
        route_name: values.route_name,
        route_description: values.route_description,
        internal_note: values.internal_note,
        route_code: values.route_code,
        ignore_capabilities: values.ignore_capabilities,
        category: values.category,
        supplier_npi: values.supplier_npi,
        network_list_id: values.network_list_id,
        ...Object.fromEntries(
            values.conditions.map((condition) => [condition.condition_type, condition.value]),
        ),
        ...RateOverrideInputs.submitValues(values),
    };

    // A product condition carries both the product and whether it is the brand
    return body[ConditionType.PRODUCT] ? { ...body, ...body[ConditionType.PRODUCT] } : body;
};
